/**
 * Create PayPal referral — what the seller posts to
 * `POST /api/paypal-account/create-referral` to kick off PayPal Partner
 * Connection onboarding. The route generates a referral URL and appends a
 * `tracking_id` query param (server-side, based on the authenticated user)
 * before redirecting the seller into PayPal.
 *
 * `sellerId` is the wire field the route reads, but the route then asserts
 * `sellerId === auth.user.id` — clients that forge a different id get 403.
 * We keep `sellerId` required (rather than pulling it purely from the
 * session) for mobile additive compatibility.
 *
 * `returnUrl` / `cancelUrl` accept any non-empty string today; we add a URL
 * shape check because the route later string-concatenates them into a
 * full URL. http(s) only — `javascript:` or `data:` URLs would otherwise
 * survive the boundary.
 *
 * Mobile-consumed (api-paypal.ts createReferral).
 *
 * @see app/api/paypal-account/create-referral/route.ts — handler
 * @see apps/mobile/lib/api-paypal.ts — mobile caller
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md H-7
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface CreateReferralRequest {
  /** Seller's `auth.users.id`. Must match the authenticated session user. */
  sellerId: string;
  /** http(s) URL PayPal redirects to on success. */
  returnUrl: string;
  /** http(s) URL PayPal redirects to on cancel. */
  cancelUrl: string;
}

/**
 * Accept only http(s) URLs. PayPal redirects break on non-HTTP schemes, and
 * accepting `javascript:` / `data:` here is a phishing footgun.
 */
const HttpUrlSchema = z
  .url()
  .refine((u) => /^https?:\/\//i.test(u), {
    message: "Must be an http(s) URL",
  });

export const CreateReferralRequestSchema = pinned<CreateReferralRequest>()(
  z.object({
    sellerId: z.string().min(1),
    returnUrl: HttpUrlSchema,
    cancelUrl: HttpUrlSchema,
  }),
);
